import PIEList from '@src/shared/components/PIEList';
import React, { ReactNode, FC, useState, useEffect } from 'react';
import DashboardVehicleSelector from '../DashboardVehicleSelector';
import DashboardCustomTag from '../DashboardCustomTag';
import PIEText from '@src/shared/components/PIEText';
import { courierStatusTranslationKeys, Statuses } from '../../models/model-constants';
import { ICourierSnap, IRoute, IWaypoint } from '../../models/model-interface';
import styles from './DashboardCourierListItem.module.scss';
import { useTranslation } from 'react-i18next';
import useDashboardComputedFunctions from '../../hooks/use-dashboard-computed';
import useEtaBack from '../../hooks/use-eta-back';
import { CourierReturnStatusType } from '@src/pages/Deliveries/context/couriers';

import dayjs from 'dayjs';

interface IDashboardCourierListItemProps {
  children?: ReactNode;
  courierItem: ICourierSnap;
  courierStatus?: any;
  courierRoute?: IRoute;
  isCourierActive?: boolean;
  onClick?: () => void;
}

export const DashboardCourierListItem: FC<IDashboardCourierListItemProps> = ({
  courierItem,
  courierStatus,
  courierRoute,
  onClick,
  isCourierActive = true,
}) => {
  /* Component States */
  const { t } = useTranslation();
  const { getCourierEtaById, getOrderEDTByUUID } = useDashboardComputedFunctions();
  const { getDistanceDuration } = useEtaBack();
  const [isOrderDelayedInPool, setIsOrderDelayedInPool] = useState(false);

  const deliveredOrderCount =
    courierRoute && courierRoute?.waypoints.filter((w: IWaypoint) => w.isDelivered).length + 1;
  const totalOrderCount = courierRoute && courierRoute.waypoints?.length;
  const getOrderUUIDforDelievered =
    courierRoute &&
    courierRoute.waypoints.filter((w: IWaypoint) => !w.isDelivered).map((o) => o.order.uuid);

  useEffect(() => {
    getOrderUUIDforDelievered &&
      getOrderUUIDforDelievered.forEach((uuid) => {
        if (getOrderEDTByUUID(uuid) && dayjs(getOrderEDTByUUID(uuid) as any).isBefore(dayjs())) {
          !isOrderDelayedInPool && setIsOrderDelayedInPool(true);
        }
      });

    return () => {
      setIsOrderDelayedInPool(false);
    };
  }, [courierRoute]);
  return (
    <PIEList.Item
      isDisabled={!isCourierActive}
      left={
        isCourierActive ? (
          <>
            <DashboardVehicleSelector
              vehicleType={courierItem.vehicleType}
              iconColor="var(--xds-color-content-subdued)"
            />
          </>
        ) : null
      }
      isLabelOnRight={courierStatus === Statuses.InDelivery}
      right={
        <div className={styles.trailing}>
          {courierStatus ? (
            <DashboardCustomTag
              tagType={courierStatus}
              modStrong={courierStatus === Statuses.Returning}
              text={
                <PIEText
                  size="var(--xds-font-size-14)"
                  truncate={false}
                  text={t(
                    courierStatus === CourierReturnStatusType.Returning
                      ? `${getDistanceDuration(getCourierEtaById(courierItem.id))}`
                      : courierStatusTranslationKeys[courierStatus]
                  )}
                />
              }
            />
          ) : null}
        </div>
      }
      linkProps={{
        onClick: () => {
          onClick && onClick();
        },
      }}
    >
      <PIEText
        text={courierItem.firstName}
        size="var(--xds-font-size-16)"
        fontWeight="var(--xds-font-weight-bold)"
        truncate={true}
      />
      <div>
        {courierStatus === Statuses.InDelivery && (totalOrderCount as number) > 1 ? (
          <span>
            <PIEText
              size="var(--xds-font-size-14)"
              truncate={true}
              color="var(--xds-color-content-subdued)"
              text={`${t('courier.status.STEP')} ${deliveredOrderCount} of ${totalOrderCount}`}
            />
          </span>
        ) : courierStatus === Statuses.InDelivery && (totalOrderCount as number) === 1 ? (
          <PIEText
            size="var(--xds-font-size-14)"
            truncate={true}
            color="var(--xds-color-content-subdued)"
            text={`${t('courier.status.SINGLE')}`}
          />
        ) : null}
      </div>
    </PIEList.Item>
  );
};

export default DashboardCourierListItem;
